import { useCallback, useEffect, useState } from "react";
import { startAuthentication, startRegistration } from "@simplewebauthn/browser";
import api from "@/lib/api";
import { toast } from "sonner";

const KEY = "mm369_passkey_hint";
export const passkeySupported = () => typeof window !== "undefined" && !!window.PublicKeyCredential;

export function useBiometric() {
  const [available, setAvailable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [creds, setCreds] = useState(null);
  const hint = typeof window !== "undefined" ? localStorage.getItem(KEY) : null;

  useEffect(() => {
    if (!passkeySupported()) return;
    window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable?.().then(setAvailable).catch(() => setAvailable(false));
  }, []);

  const loadCreds = useCallback(() => api.get("/webauthn/credentials").then(r => setCreds(r.data.credentials)).catch(() => setCreds([])), []);

  const enroll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/webauthn/register/options");
      const credential = await startRegistration({ optionsJSON: data.options });
      const res = await api.post("/webauthn/register/verify", { token: data.token, credential });
      localStorage.setItem(KEY, "1");
      toast.success("Biometric unlock enabled", { description: `This ${res.data.label} can now sign in with fingerprint / Face ID.` });
      loadCreds(); window.dispatchEvent(new Event("mm369:webauthn-changed")); return true;
    } catch (e) {
      if (e?.name === "NotAllowedError") toast.info("Biometric setup cancelled");
      else if (e?.name === "InvalidStateError") toast.info("This device is already registered");
      else toast.error("Could not enable biometrics", { description: e.response?.data?.detail || e.message });
      return false;
    } finally { setBusy(false); }
  };

  const unlock = async (remember = true) => {
    setBusy(true);
    try {
      const { data } = await api.post("/webauthn/login/options");
      const credential = await startAuthentication({ optionsJSON: data.options });
      const res = await api.post("/webauthn/login/verify", { token: data.token, credential, remember });
      localStorage.setItem(KEY, "1");
      return res.data;
    } catch (e) {
      if (e?.name === "NotAllowedError") toast.info("Biometric unlock cancelled — use your password instead");
      else toast.error("Biometric unlock failed", { description: e.response?.data?.detail || e.message });
      return null;
    } finally { setBusy(false); }
  };

  const remove = async (cid) => { await api.delete(`/webauthn/credentials/${cid}`); if ((creds || []).length <= 1) localStorage.removeItem(KEY); loadCreds(); window.dispatchEvent(new Event("mm369:webauthn-changed")); };

  useEffect(() => { const h = () => loadCreds(); window.addEventListener("mm369:webauthn-changed", h); return () => window.removeEventListener("mm369:webauthn-changed", h); }, [loadCreds]);

  return { supported: passkeySupported(), available, busy, hint: !!hint, creds, loadCreds, enroll, unlock, remove };
}
